import { useCallback, useState } from 'react';

import { Api } from 'src/models/Api.model';
import { loadSessionsWithExtraData } from 'src/models/GalenData/helpers/loadSessionsWithExtraData';
import { SortOrder } from 'src/models/GalenData/SortOrder.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useGetRetinalExamDataModelId from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

const useLoadSessions = () => {
  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { tCommon, language } = useUiTranslation();
  const [currentSearchValue, setCurrentSearchValue] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSetCurrentSearchValue = useCallback((searchValue: string) => {
    setCurrentSearchValue(searchValue);
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const { deviceDataModelId } = useGetRetinalExamDataModelId();

  const [dataResource, setDataResource] = useState<{
    sessions: Session[];
    totalElements: number;
    totalPages: number;
  }>({
    sessions: [],
    totalElements: 0,
    totalPages: 0,
  });

  const handleLoadSessions = useCallback(
    async (
      page: number,
      pageSize: number,
      searchValue: string,
      sessionsSortOrder: SortOrder,
    ) => {
      try {
        setIsLoading(true);

        const res = await loadSessionsWithExtraData(
          deviceDataModelId,
          page,
          pageSize,
          searchValue,
          sessionsSortOrder,
        );

        setDataResource(res);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);

        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message:
            tCommon('message.error.failedToLoadSessions') + ' ' + errorMessage,

          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [deviceDataModelId, handleHttpRequestError, language, tCommon],
  );

  return {
    currentPage,
    currentSearchValue,
    handleSetCurrentPage,
    handleSetCurrentSearchValue,
    isLoading,
    dataResource,
    handleLoadSessions,
  };
};

export default useLoadSessions;
